import { Chat, Close } from '@mui/icons-material'
import { Fab, Fade, Popper } from '@mui/material'
import { useRef, useState } from 'react'
import AiChat from './ai-chat'
import ChatSessionList from './chat-session-list'

import { useAiConnection } from 'api/ai/use-ai-connection'
import { cx } from 'class-variance-authority'
import { useHasRole } from 'hooks/auth/use-has-role'
import { useFlags } from 'launchdarkly-react-client-sdk'
import styles from './ai-chat-widget.module.css'
import { useChatSessions } from './use-chat-sessions'

export const AiChatWidget = () => {
	const anchorRef = useRef<HTMLButtonElement | null>(null)
	const [openAi, setOpenAi] = useState(false)
	const hasRole = useHasRole('admin ai user')
	const { enable_corestream_admin_ai } = useFlags()

	const showWidget = enable_corestream_admin_ai && hasRole

	const {
		sessions,
		activeSession,
		createSession,
		selectSession,
		goToList,
		deleteSession,
		addUserMessage,
		addAssistantMessage,
		appendMessage,
		completeMessage,
	} = useChatSessions()

	const {
		initialize: initializeConnection,
		isLoading,
		sendPrompt,
	} = useAiConnection({
		onReceiveMessage: (sessionId, msg) => addAssistantMessage(msg.content, true, sessionId),
	})

	const onSendMessage = (text: string) => {
		if (!activeSession) {
			return
		}

		const sessionId = activeSession.id
		addUserMessage(text)
		const messageId = addAssistantMessage('', false, sessionId)

		if (!messageId) {
			return
		}

		sendPrompt(sessionId, text, {
			onNext: (res) => appendMessage(messageId, res.content, sessionId),
			onComplete: () => completeMessage(messageId, sessionId),
		})
	}

	const onNewChat = () => {
		const sessionId = createSession()
		initializeConnection(sessionId)
	}

	const onSelectSession = (id: string) => {
		selectSession(id)
		initializeConnection(id)
	}

	const togglePopper = () => {
		setOpenAi(!openAi)
	}

	if (!showWidget) {
		return null
	}

	return (
		<>
			<div
				className={cx(styles['chat-fab-container'], {
					[styles['development']]: process.env.NODE_ENV === 'development',
				})}
			>
				<Fab className={styles['fab']} color='secondary' size='medium' ref={anchorRef} onClick={togglePopper}>
					{openAi ? <Close className={styles['fab-icon']} /> : <Chat className={styles['fab-icon']} />}
				</Fab>
			</div>
			<Popper
				className={styles['chat-popper']}
				anchorEl={anchorRef.current}
				open={openAi}
				placement='top-end'
				transition
			>
				{({ TransitionProps, placement }) => (
					<Fade {...TransitionProps} timeout={100}>
						<div className={styles['chat-popup-container']} data-placement={placement}>
							{activeSession ? (
								<AiChat
									isLoading={isLoading(activeSession.id)}
									messages={activeSession.messages}
									onBack={goToList}
									onSendMessage={onSendMessage}
									sessionId={activeSession.id}
								/>
							) : (
								<ChatSessionList
									sessions={sessions}
									onSelectSession={onSelectSession}
									onDeleteSession={deleteSession}
									onNewChat={onNewChat}
								/>
							)}
						</div>
					</Fade>
				)}
			</Popper>
		</>
	)
}
